import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Search, KanbanSquare, Sparkles, FileBarChart, BellRing,
  Send, Users, LogOut, ChevronRight, CheckSquare, Upload, UserPlus,
  CreditCard, Settings as SettingsIcon, UserCog, Gift, ShieldCheck, DollarSign, Database, Inbox,
  Radar, Activity, FileText, Globe, ScrollText, HelpCircle, Megaphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { tierLabel, isBusinessTier, isPaidTier } from "@/lib/tiers";
import HotLeadBanner from "@/components/HotLeadBanner";
import FCRAAckModal from "@/components/FCRAAckModal";
import MfaNudgeBanner from "@/components/MfaNudgeBanner";

/**
 * Sidebar branding lives in the rendered <img> below. The old AqMarkSmall SVG
 * (a green "AQ" tile fallback) was removed in iter 21. The real PNG logo
 * always serves; we only fall back to the static /images/acquigine-logo.png
 * if the tenant brand_url 404s.
 */

const PRIMARY_NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/intake", label: "Add Opportunity", icon: UserPlus, testid: "nav-intake" },
  { to: "/app/opportunities", label: "Opportunities", icon: Search, testid: "nav-opportunities" },
  { to: "/app/leads", label: "Leads", icon: Users, testid: "nav-leads" },
  { to: "/app/pipeline", label: "Pipeline", icon: KanbanSquare, testid: "nav-pipeline" },
  { to: "/app/assistant", label: "AI Assistant", icon: Sparkles, testid: "nav-assistant", tierLock: "pro" },
  { to: "/app/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks" },
  { to: "/app/outreach", label: "Outreach", icon: Send, testid: "nav-outreach", tierLock: "pro" },
  { to: "/app/alerts", label: "Alerts", icon: BellRing, testid: "nav-alerts" },
  { to: "/app/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports" },
  { to: "/app/documents/library", label: "State Requirements", icon: FileText, testid: "nav-state-requirements", tierLock: "pro" },
  { to: "/app/import", label: "Bulk Import", icon: Upload, testid: "nav-import", tierLock: "business" },
  { to: "/app/referrals", label: "Refer & Earn", icon: Gift, testid: "nav-referrals" },
];

const FOOTER_NAV = [
  { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/app/help", label: "Help & Guide", icon: HelpCircle, testid: "nav-help" },
];

const ADMIN_NAV = [
  { to: "/app/users", label: "Users", icon: UserCog, testid: "nav-users", roles: ["admin", "super_admin", "manager"] },
  { to: "/app/admin/referrals", label: "Payouts", icon: DollarSign, testid: "nav-admin-referrals", roles: ["admin", "super_admin"] },
  { to: "/app/admin/audit", label: "Audit Log", icon: ShieldCheck, testid: "nav-admin-audit", roles: ["admin", "super_admin", "manager"] },
  { to: "/app/admin/data-sources", label: "Data Sources", icon: Database, testid: "nav-admin-data-sources", roles: ["admin", "super_admin", "manager"] },
  { to: "/app/admin/discovery", label: "Lead Discovery", icon: Radar, testid: "nav-admin-discovery", roles: ["admin", "super_admin"] },
  { to: "/app/admin/discovery/configs", label: "County Connectors", icon: Database, testid: "nav-admin-discovery-configs", roles: ["admin", "super_admin"] },
  { to: "/app/admin/submissions", label: "Pending Review", icon: Inbox, testid: "nav-admin-submissions", roles: ["admin", "super_admin", "manager"], pendingBadge: true },
  { to: "/app/admin/seo", label: "SEO", icon: Globe, testid: "nav-admin-seo", roles: ["admin", "super_admin"] },
  { to: "/app/admin/legal-pages", label: "Legal Pages", icon: FileText, testid: "nav-admin-legal-pages", roles: ["admin", "super_admin"] },
  { to: "/app/admin/legal-sourcing", label: "Legal Sourcing", icon: ScrollText, testid: "nav-admin-legal-sourcing", roles: ["admin", "super_admin", "manager"] },
  { to: "/app/admin/platform-ops", label: "Platform Ops", icon: SettingsIcon, testid: "nav-admin-platform-ops", roles: ["admin", "super_admin"] },
  { to: "/app/admin/attribution", label: "Signup Attribution", icon: Megaphone, testid: "nav-admin-attribution", roles: ["super_admin"] },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [brand, setBrand] = useState({ brand_name: "Acquigine", logo_url: "", primary_color: "#10B981", tagline: "Acquisition Intelligence Platform" });
  const [logoFailed, setLogoFailed] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    api.get("/admin/brand").then((r) => { setBrand(r.data); setLogoFailed(false); }).catch(() => {});
  }, []);

  // Iter 58: poll the Ready-to-Contact queue count so the Outreach nav
  // item can show a badge count next to it. Polls every 30s.
  const [readyCount, setReadyCount] = useState(0);
  useEffect(() => {
    if (!user) return;
    const isPaid = ["admin", "super_admin"].includes(user.role) || isPaidTier(user.tier);
    if (!isPaid) return;
    let mounted = true;
    const load = () => {
      api.get("/outreach-queue/ready/count").then((r) => {
        if (mounted) setReadyCount(r.data.count || 0);
      }).catch(() => {});
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(iv); };
  }, [user]);

  // BLOCK 16: poll the source-agnostic pending-review count so the admin
  // Pending Review nav item shows a badge with the current queue depth.
  const [pendingSubs, setPendingSubs] = useState(0);
  useEffect(() => {
    if (!user) return;
    if (!["admin", "super_admin", "manager"].includes(user.role)) return;
    let mounted = true;
    const load = () => {
      api.get("/listings/submissions/pending-count").then((r) => {
        if (mounted) setPendingSubs(r.data?.count || 0);
      }).catch(() => {});
    };
    load();
    // Refresh when the tab regains focus so an admin who just approved
    // an item on the inbox page sees the sidebar count drop when they
    // navigate away.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    // Also listen to a custom event that SubmissionsInbox dispatches
    // right after a decision so the count updates without waiting for
    // focus / poll.
    const onDecision = () => load();
    window.addEventListener("acq:pending-submissions-changed", onDecision);
    const iv = setInterval(load, 30000);
    return () => {
      mounted = false;
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("acq:pending-submissions-changed", onDecision);
    };
  }, [user]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // BLOCK 167 ,  seller accounts never see this subscriber/admin shell
  // (its nav points at endpoints they're locked out of server-side).
  if (user.role === "seller") {
    return <Navigate to="/seller/dashboard" replace />;
  }

  const adminItems = ADMIN_NAV.filter((n) => n.roles.includes(user.role)).map((n) => {
    let countBadge = null;
    if (n.pendingBadge && pendingSubs > 0) countBadge = pendingSubs;
    return { ...n, countBadge };
  });
  const tierIsBusiness = isBusinessTier(user.tier) || ["admin", "super_admin"].includes(user.role);
  const tierIsPro = (user.tier || "").toLowerCase() === "pro" || tierIsBusiness;
  const active = user.subscription_status !== "canceled";
  // Iter 53: keep all nav items visible; badge locked ones so users see
  // exactly what upgrading unlocks instead of hiding the entry.
  const primaryItems = PRIMARY_NAV.map((n) => {
    let badge = null;
    if (n.tierLock === "pro" && (!tierIsPro || !active)) badge = "PRO";
    if (n.tierLock === "business" && (!tierIsBusiness || !active)) badge = "BUSINESS";
    // Iter 58: Ready-to-Contact queue badge on Outreach.
    let countBadge = null;
    if (n.to === "/app/outreach" && tierIsPro && active && readyCount > 0) {
      countBadge = readyCount;
    }
    return { ...n, badge, countBadge };
  });

  return (
    <div className="h-screen flex bg-[#05070A] text-slate-100 overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/8 bg-[#05070A] flex flex-col h-screen">
        <div className="px-4 py-5 border-b border-white/8 flex items-center justify-center shrink-0">
          <img
            src={brand.logo_url && !logoFailed ? brand.logo_url : "/images/acquigine-logo.png"}
            alt={brand.brand_name || "Acquigine"}
            onError={() => setLogoFailed(true)}
            className="h-20 md:h-[88px] w-auto object-contain"
            data-testid="brand-logo"
          />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {primaryItems.map((item) => <NavItem key={item.to} {...item} />)}
          {adminItems.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-slate-600 mt-4 mb-2 px-3">Admin</div>
              {adminItems.map((item) => <NavItem key={item.to} {...item} />)}
            </>
          )}
          <div className="text-[10px] uppercase tracking-widest text-slate-600 mt-4 mb-2 px-3">Account</div>
          {FOOTER_NAV.map((item) => <NavItem key={item.to} {...item} />)}
        </nav>

        <div className="px-3 py-3 border-t border-white/8 shrink-0">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="h-8 w-8 rounded-sm bg-white/8 flex items-center justify-center font-mono text-xs">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate" data-testid="user-name">{user.name}</div>
              <div className="flex items-center gap-1.5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">{user.role.replace("_", " ")}</div>
                {user.tier && user.tier !== "free" && (
                  <span className="text-[9px] font-mono uppercase bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-1 rounded-sm" data-testid="tier-badge">
                    {tierLabel(user.tier)}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="p-1.5 rounded-sm text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 h-screen overflow-y-auto" data-testid="admin-main-scroll">
        <div className="glass-nav sticky top-0 z-30 px-8 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 pulse-dot" />
            <span className="text-emerald-400">Live · {now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
            <span className="text-emerald-400 tabular-nums tracking-normal normal-case" data-testid="header-local-time">
              {now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
            <ChevronRight className="h-3 w-3 opacity-40" />
            <span className="text-slate-400 normal-case tracking-normal">{brand.tagline}</span>
          </div>
          <a
            href="/sell"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="open-funnel-link"
            className="text-xs uppercase tracking-widest text-emerald-400 hover:text-emerald-300 transition-colors"
          >
            Seller Funnel →
          </a>
        </div>
        <div className="p-8 max-w-[1600px] mx-auto">
          <MfaNudgeBanner />
          <HotLeadBanner />
          <Outlet />
        </div>
      </main>
      <FCRAAckModal />
    </div>
  );
}

function NavItem({ to, label, icon: Icon, testid, badge, countBadge }) {
  const navigate = useNavigate();
  const handleBadgeClick = (e) => {
    if (!badge) return;
    e.preventDefault();
    e.stopPropagation();
    const planParam = badge === "BUSINESS" ? "business" : "pro";
    navigate(`/app/billing?plan=${planParam}`);
  };
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-all ${
          isActive
            ? "bg-emerald-500/10 text-emerald-300 border-l-2 border-emerald-500 pl-[10px]"
            : "text-slate-400 hover:text-slate-100 hover:bg-white/[0.03]"
        }`
      }
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1 truncate">{label}</span>
      {countBadge != null && countBadge > 0 && (
        <span
          data-testid={`${testid}-count-badge`}
          title={`${countBadge} lead${countBadge === 1 ? "" : "s"} waiting for review`}
          className="px-1.5 py-0.5 text-[10px] font-mono font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-sm"
        >
          {countBadge > 99 ? "99+" : countBadge}
        </span>
      )}
      {badge && (
        <button
          type="button"
          onClick={handleBadgeClick}
          data-testid={`${testid}-tier-badge`}
          title={`${badge} feature ,  click to upgrade`}
          className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-mono border border-slate-700 bg-slate-800/60 text-slate-400 rounded-sm hover:border-emerald-500/40 hover:text-emerald-300 transition-colors cursor-pointer"
        >
          {badge}
        </button>
      )}
    </NavLink>
  );
}
